/**
 * AdminAgentAnalytics - Admin dashboard for Contract Expert analytics
 * 
 * Phase 5: Cost monitoring, workspace activity, performance metrics,
 * health scoring, and billing readiness tracking.
 */

import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Users,
  FileText,
  MessageSquare,
  Mic,
  TrendingUp,
  DollarSign,
  Activity,
  Heart,
  AlertTriangle,
  Download,
  RefreshCw,
  Building2,
  Send,
  MessageCircle,
} from "lucide-react";
import { toast } from "sonner";

// Create admin-specific axios instance
const adminApi = axios.create({
  baseURL: '/api',
  timeout: 30000,
  headers: { "Content-Type": "application/json" }
});

// Add admin token to requests
adminApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("admin_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export default function AdminAgentAnalytics() {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [workspaceTotal, setWorkspaceTotal] = useState(0);
  const [costSummary, setCostSummary] = useState(null);
  const [healthDistribution, setHealthDistribution] = useState(null);
  const [performance, setPerformance] = useState(null);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("last_activity");
  const [statusFilter, setStatusFilter] = useState("all");
  const [periodDays, setPeriodDays] = useState(30);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await adminApi.get("/admin/agent/analytics/dashboard");
      setDashboard(res.data);
    } catch (err) {
      console.error("Failed to fetch dashboard:", err);
    }
  }, []);

  const fetchWorkspaces = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        sort_by: sortBy,
        sort_order: "desc",
        limit: "50",
        offset: "0"
      });
      
      if (searchTerm) params.append("search", searchTerm);
      if (statusFilter !== "all") params.append("status", statusFilter);
      
      const res = await adminApi.get(`/admin/agent/analytics/workspaces?${params}`);
      setWorkspaces(res.data.workspaces || []);
      setWorkspaceTotal(res.data.total || 0);
    } catch (err) {
      console.error("Failed to fetch workspaces:", err);
    }
  }, [searchTerm, sortBy, statusFilter]);

  const fetchCostSummary = useCallback(async () => {
    try {
      const res = await adminApi.get(`/admin/agent/analytics/costs/summary?period_days=${periodDays}`);
      setCostSummary(res.data);
    } catch (err) {
      console.error("Failed to fetch cost summary:", err);
    }
  }, [periodDays]);

  const fetchHealth = useCallback(async () => {
    try {
      const res = await adminApi.get("/admin/agent/analytics/health/distribution");
      setHealthDistribution(res.data);
    } catch (err) {
      console.error("Failed to fetch health:", err);
    }
  }, []);

  const fetchPerformance = useCallback(async () => {
    try {
      const res = await adminApi.get("/admin/agent/analytics/performance?period=day");
      setPerformance(res.data);
    } catch (err) {
      console.error("Failed to fetch performance:", err);
    }
  }, []);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([
        fetchDashboard(),
        fetchWorkspaces(),
        fetchCostSummary(),
        fetchHealth(),
        fetchPerformance()
      ]);
      setLoading(false);
    };
    loadAll();
  }, [fetchDashboard, fetchWorkspaces, fetchCostSummary, fetchHealth, fetchPerformance]);

  const handleExport = async (type) => {
    try {
      const response = await adminApi.get(`/admin/agent/analytics/export/${type}?format=csv`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Export downloaded successfully");
    } catch (err) {
      toast.error("Failed to export data");
    }
  };

  const refreshData = async () => {
    setLoading(true);
    await Promise.all([
      fetchDashboard(),
      fetchWorkspaces(),
      fetchCostSummary(),
      fetchHealth(),
      fetchPerformance()
    ]);
    setLoading(false);
    toast.success("Data refreshed");
  };

  const globalMetrics = dashboard?.global_metrics || {};
  const growthMetrics = dashboard?.growth_metrics || {};

  // Show loading state
  if (loading && !dashboard) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-agent-analytics">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Contract Expert Analytics</h1>
            <p className="text-muted-foreground">
              Monitor usage, costs, performance, and workspace health
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={refreshData} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Select onValueChange={(v) => handleExport(v)}>
              <SelectTrigger className="w-40">
                <Download className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Export" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="workspace-usage">Workspace Usage</SelectItem>
                <SelectItem value="cost-data">Cost Data</SelectItem>
                <SelectItem value="activity-report">Activity Report</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Global Metrics Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <MetricCard
            title="Workspaces"
            value={globalMetrics.total_workspaces || 0}
            subtext={`${globalMetrics.active_workspaces || 0} active`}
            icon={Building2}
          />
          <MetricCard
            title="Users"
            value={globalMetrics.total_users || 0}
            subtext={`${globalMetrics.active_users || 0} active`}
            icon={Users}
          />
          <MetricCard
            title="Documents"
            value={globalMetrics.total_documents || 0}
            icon={FileText}
          />
          <MetricCard
            title="Questions"
            value={globalMetrics.total_questions || 0}
            icon={MessageSquare}
          />
          <MetricCard
            title="Voice Notes"
            value={globalMetrics.total_voice_notes || 0}
            icon={Mic}
          />
          <MetricCard
            title="Channels"
            value={globalMetrics.total_channel_connections || 0}
            subtext={`${globalMetrics.telegram_connections || 0} TG / ${globalMetrics.whatsapp_connections || 0} WA`}
            icon={Send}
          />
        </div>

        {/* Growth and Health Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Growth Metrics */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Active Users
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <div className="text-2xl font-bold">{growthMetrics.daily_active_users || 0}</div>
                  <div className="text-xs text-muted-foreground">DAU</div>
                </div>
                <div>
                  <div className="text-2xl font-bold">{growthMetrics.weekly_active_users || 0}</div>
                  <div className="text-xs text-muted-foreground">WAU</div>
                </div>
                <div>
                  <div className="text-2xl font-bold">{growthMetrics.monthly_active_users || 0}</div>
                  <div className="text-xs text-muted-foreground">MAU</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Health Distribution */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Heart className="w-4 h-4" />
                Workspace Health
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <div className="text-2xl font-bold text-green-600">
                    {healthDistribution?.healthy_count || 0}
                  </div>
                  <div className="text-xs text-muted-foreground">Healthy</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-amber-600">
                    {healthDistribution?.at_risk_count || 0}
                  </div>
                  <div className="text-xs text-muted-foreground">At Risk</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-400">
                    {healthDistribution?.inactive_count || 0}
                  </div>
                  <div className="text-xs text-muted-foreground">Inactive</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Cost Summary */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                Cost ({periodDays}d)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                ${(costSummary?.total_cost_usd || 0).toFixed(4)}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {costSummary?.workspaces_with_usage || 0} workspaces with usage
              </div>
              {costSummary?.by_category && (
                <div className="grid grid-cols-2 gap-1 mt-2 text-xs">
                  <span>LLM: ${costSummary.by_category.llm_chat?.cost_usd?.toFixed(4) || '0.0000'}</span>
                  <span>Embed: ${costSummary.by_category.embedding?.cost_usd?.toFixed(4) || '0.0000'}</span>
                  <span>Voice: ${costSummary.by_category.transcription?.cost_usd?.toFixed(4) || '0.0000'}</span>
                  <span>OCR: ${costSummary.by_category.ocr?.cost_usd?.toFixed(4) || '0.0000'}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Performance Metrics */}
        {performance && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Activity className="w-4 h-4" />
                Performance (24h)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
                <div>
                  <div className="text-xl font-bold">{performance.total_responses}</div>
                  <div className="text-xs text-muted-foreground">Responses</div>
                </div>
                <div>
                  <div className="text-xl font-bold text-green-600">{performance.success_rate}%</div>
                  <div className="text-xs text-muted-foreground">Success Rate</div>
                </div>
                <div>
                  <div className="text-xl font-bold">{performance.avg_response_time}s</div>
                  <div className="text-xs text-muted-foreground">Avg Response</div>
                </div>
                <div>
                  <div className="text-xl font-bold">{performance.avg_citations_provided}</div>
                  <div className="text-xs text-muted-foreground">Avg Citations</div>
                </div>
                <div>
                  <div className="text-xl font-bold text-red-600">
                    {(performance.transcription_failures || 0) + 
                     (performance.ocr_failures || 0) + 
                     (performance.upload_failures || 0)}
                  </div>
                  <div className="text-xs text-muted-foreground">Failures</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Workspace Activity Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Workspace Activity</CardTitle>
              <div className="flex gap-2">
                <Input
                  placeholder="Search workspaces..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-48"
                  data-testid="workspace-search"
                />
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-32">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-36">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="last_activity">Last Activity</SelectItem>
                    <SelectItem value="created">Created</SelectItem>
                    <SelectItem value="name">Name</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={fetchWorkspaces}>
                  Apply
                </Button>
              </div>
            </div>
            <CardDescription>
              {workspaceTotal} total workspaces
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Workspace</TableHead>
                  <TableHead>Owner</TableHead>
                  <TableHead className="text-center">Users</TableHead>
                  <TableHead className="text-center">Docs</TableHead>
                  <TableHead className="text-center">Questions</TableHead>
                  <TableHead className="text-center">Voice</TableHead>
                  <TableHead className="text-center">Channels</TableHead>
                  <TableHead className="text-center">Cost</TableHead>
                  <TableHead>Last Activity</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {workspaces.map((ws) => (
                  <TableRow key={ws.workspace_id} data-testid={`workspace-row-${ws.workspace_id}`}>
                    <TableCell className="font-medium">{ws.workspace_name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {ws.owner_email}
                    </TableCell>
                    <TableCell className="text-center">{ws.total_users}</TableCell>
                    <TableCell className="text-center">{ws.documents_uploaded}</TableCell>
                    <TableCell className="text-center">{ws.total_questions}</TableCell>
                    <TableCell className="text-center">{ws.voice_notes_processed}</TableCell>
                    <TableCell className="text-center">
                      <div className="flex justify-center gap-1">
                        {ws.telegram_connected && (
                          <Badge variant="outline" className="text-xs">TG</Badge>
                        )}
                        {ws.whatsapp_connected && (
                          <Badge variant="outline" className="text-xs">WA</Badge>
                        )}
                        {!ws.telegram_connected && !ws.whatsapp_connected && (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      ${ws.estimated_monthly_cost?.toFixed(4) || '0.0000'}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {ws.last_activity ? new Date(ws.last_activity).toLocaleDateString() : '-'}
                    </TableCell>
                  </TableRow>
                ))}
                {workspaces.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center text-muted-foreground py-8">
                      No workspaces found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
  );
}

function MetricCard({ title, value, subtext, icon: Icon }) {
  return (
    <Card>
      <CardContent className="pt-4">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-muted-foreground" />
          <span className="text-xs text-muted-foreground">{title}</span>
        </div>
        <div className="text-2xl font-bold mt-1">{value}</div>
        {subtext && (
          <div className="text-xs text-muted-foreground">{subtext}</div>
        )}
      </CardContent>
    </Card>
  );
}
